import { Link } from "react-router";

import { UserLink } from "@/shared/components";
import { TriggerLink } from "@/shared/components/links/TriggerLink";
import {
  FormattedDateTime,
  formatRelativeTime,
  isRecent,
  toDate,
  useDateTimeFormat,
} from "@/shared/datetime";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";
import { ArrowRight } from "@/shared/foundation/icons";
import { Spinner, Table } from "@/shared/foundation/primitives";
import { useRelativeTick } from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import { ThreadStatusBadge } from "../../Threads/ThreadStatusBadge";

import styles from "./sections.module.css";

export interface RecentThreadsTableProps {
  agentId: string;
  /** The threads to list, newest first; `undefined` while they load. */
  threads: AgentThreadRecord[] | undefined;
}

/**
 * The agent's latest threads, one row each. Rows are inert: the title is the
 * way into a thread, and the footer is the way to all of them.
 */
export function RecentThreadsTable({
  agentId,
  threads,
}: RecentThreadsTableProps) {
  return (
    <Table>
      <Table.Header variant="compact">
        <Table.Row>
          <Table.Head scope="col">Title</Table.Head>
          <Table.Head scope="col">ID</Table.Head>
          <Table.Head scope="col">Status</Table.Head>
          <Table.Head scope="col">Started</Table.Head>
          <Table.Head scope="col">Triggered by</Table.Head>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {threads === undefined ? (
          <Table.Row data-testid="agent-detail-threads-loading">
            <Table.Cell colSpan={5}>
              <p className={styles.loading}>
                <Spinner aria-hidden />
                Loading threads&hellip;
              </p>
            </Table.Cell>
          </Table.Row>
        ) : (
          threads.map((thread) => (
            <Table.Row
              key={thread.thread_id}
              data-testid="agent-detail-thread"
              data-thread-id={thread.thread_id}
            >
              <Table.Cell>
                <Link
                  to={Goto.createAgentThreadRoute(thread.thread_id)}
                  className={styles.threadTitle}
                >
                  {thread.title ?? thread.thread_id}
                </Link>
              </Table.Cell>
              <Table.Cell className={styles.threadId}>
                {thread.thread_id}
              </Table.Cell>
              <Table.Cell>
                <ThreadStatusBadge status={thread.status} />
              </Table.Cell>
              <Table.Cell>
                <StartedAt value={thread.created} />
              </Table.Cell>
              <Table.Cell>
                {thread.created_from_trigger_id ? (
                  <TriggerLink triggerId={thread.created_from_trigger_id} />
                ) : (
                  <UserLink userId={thread.created_by} />
                )}
              </Table.Cell>
            </Table.Row>
          ))
        )}
      </Table.Body>
      {threads !== undefined && threads.length > 0 && (
        <Table.Footer>
          <Table.Row>
            <Table.Cell colSpan={5} className={styles.threadsAllCell}>
              <Link
                to={Goto.createAgentThreadsRoute(agentId)}
                className={styles.threadsAll}
                data-testid="agent-detail-threads-all"
              >
                View all threads
                <ArrowRight
                  aria-hidden
                  size={16}
                  className={styles.threadsAllArrow}
                />
              </Link>
            </Table.Cell>
          </Table.Row>
        </Table.Footer>
      )}
    </Table>
  );
}

/**
 * When a thread started: "5 minutes ago" for the last day, ticking as it
 * ages, then the date and time.
 */
function StartedAt({ value }: { value: string }) {
  const dateTimeFormat = useDateTimeFormat();
  const date = toDate(value);
  const recent = date !== null && isRecent(date);
  useRelativeTick(recent);

  return (
    <time
      dateTime={value}
      title={recent ? dateTimeFormat.format(date) : undefined}
    >
      {recent ? formatRelativeTime(date) : <FormattedDateTime value={value} />}
    </time>
  );
}
